import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import type { ApiResponse } from "types/api.types";
import { formatCurrency } from "utils/currency";
import { DateOnly } from "components/common/Date";

export type AdvanceSummaryParams = {
  year?: number;
  month?: number;
  from?: string;
  to?: string;
};

export interface AdvanceSummaryTotals {
  totalCount: number;
  totalAmount: number;
  /** Sum of (amount - settled) across PROCESSED-status advances only. */
  outstandingAmount: number;
  settledAmount: number;
  refundedAmount: number;
}

export interface AdvanceSummaryByStatusRow {
  status: string;
  count: number;
  totalAmount: number;
}

export interface AdvanceSummaryData {
  scope: "all-time" | "period";
  period: { start: string; end: string } | null;
  totals: AdvanceSummaryTotals;
  byStatus: AdvanceSummaryByStatusRow[];
}

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type Props = {
  fetchSummary: (
    params: AdvanceSummaryParams,
  ) => Promise<ApiResponse<AdvanceSummaryData>>;
  statusLabelMap: Record<string, { label: string; color: string }>;
};

/** Shared by Client Advance and Employee Advance lists — identical response shape on both. */
export default function AdvanceSummaryWidgets({
  fetchSummary,
  statusLabelMap,
}: Props) {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [allTime, setAllTime] = useState(true);
  const [data, setData] = useState<AdvanceSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    const out: number[] = [];
    for (let i = y - 5; i <= y + 2; i += 1) out.push(i);
    return out;
  }, [now]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSummary(allTime ? {} : { year, month });
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res.message || "Could not load summary.");
      }
    } catch (err) {
      setData(null);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not load summary.";
      setError(typeof msg === "string" ? msg : "Could not load summary.");
    } finally {
      setLoading(false);
    }
  }, [fetchSummary, allTime, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = data?.totals;
  const byStatus = data?.byStatus ?? [];
  const period = data?.period;

  return (
    <div className="mb-4">
      <Row className="g-3 align-items-end mb-3">
        <Col xs="auto">
          <Label className="form-label text-muted small mb-1 d-block">
            Summary period
          </Label>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Input
              type="select"
              bsSize="sm"
              className="w-auto"
              value={allTime ? "all-time" : "period"}
              onChange={(e) => setAllTime(e.target.value === "all-time")}
              aria-label="Scope"
            >
              <option value="all-time">All-time</option>
              <option value="period">Specific month</option>
            </Input>
            {!allTime ? (
              <>
                <Input
                  type="select"
                  bsSize="sm"
                  className="w-auto"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  aria-label="Month"
                >
                  {MONTH_LABELS.map((label, i) => (
                    <option key={label} value={i + 1}>
                      {label}
                    </option>
                  ))}
                </Input>
                <Input
                  type="select"
                  bsSize="sm"
                  className="w-auto"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  aria-label="Year"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </>
            ) : null}
          </div>
        </Col>
        <Col className="text-muted small">
          {period?.start && period?.end ? (
            <>
              Created from <DateOnly date={period.start} /> to{" "}
              <DateOnly date={period.end} />
            </>
          ) : allTime ? (
            "All advances, regardless of when created"
          ) : null}
        </Col>
      </Row>

      {error ? (
        <Alert color="danger" className="mb-0">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <Spinner size="sm" />
          <span>Loading summary…</span>
        </div>
      ) : data && totals ? (
        <>
          <Row className="g-3 mb-3">
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Total ({totals.totalCount.toLocaleString("en-IN")})
                  </p>
                  <h4 className="mb-0 tabular-nums">
                    {formatCurrency(totals.totalAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Outstanding
                  </p>
                  <h4 className="mb-0 text-warning tabular-nums">
                    {formatCurrency(totals.outstandingAmount, {
                      compact: true,
                    })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Settled
                  </p>
                  <h4 className="mb-0 text-success tabular-nums">
                    {formatCurrency(totals.settledAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Refunded
                  </p>
                  <h4 className="mb-0 text-dark tabular-nums">
                    {formatCurrency(totals.refundedAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="border shadow-none">
            <CardBody className="py-3">
              <h6 className="text-muted text-uppercase small mb-3">
                By status
              </h6>
              {byStatus.length === 0 ? (
                <p className="text-muted mb-0 small">
                  No advances in this period.
                </p>
              ) : (
                <div className="table-responsive mb-0">
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light text-muted small">
                      <tr>
                        <th scope="col">Status</th>
                        <th scope="col" className="text-end">
                          Amount
                        </th>
                        <th scope="col" className="text-end">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {byStatus.map((row) => {
                        const info = statusLabelMap[row.status];
                        return (
                          <tr key={row.status}>
                            <td>
                              <span
                                className={`badge bg-${info?.color ?? "secondary"}-subtle text-${info?.color ?? "secondary"}`}
                              >
                                {info?.label ?? row.status}
                              </span>
                            </td>
                            <td className="text-end tabular-nums fw-medium">
                              {formatCurrency(row.totalAmount)}
                            </td>
                            <td className="text-end tabular-nums text-muted">
                              {row.count.toLocaleString("en-IN")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      ) : null}
    </div>
  );
}
